import { instanceToPlain, plainToInstance } from 'class-transformer';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Person, PersonProfile } from '../../entities';
import FetchError from '../../errors/fetch.error';
import { Collections, db, functions } from '../../utils/firebase.config';
import { converterOf } from '../../utils/firebase.utils';
import EmailValidate from '../../entities/email-validate';

export interface NewPerson extends Omit<Person, 'id' | 'photoUrl'> {
  password: string;
}

export type UpdatePerson = Partial<Person> & { id: string };

export const usePersons = () => {
  const personsCollection = collection(db, Collections.Persons).withConverter(converterOf(Person));

  const fetchById = async (id: string): Promise<Person> => {
    const personDoc = doc(personsCollection, id);

    const resource = await getDoc(personDoc);

    const data = resource.data();

    if (!data) {
      throw new FetchError({ trace: 'fetchById', message: `Não foi possível carregar um usuário com o id: ${id}` });
    }

    return data;
  };

  const fetchProfilesOf = async (id: string): Promise<PersonProfile[]> => {
    const personDoc = doc(personsCollection, id);

    const profilesCollection = collection(personDoc, Collections.Profiles).withConverter(converterOf(PersonProfile));

    const resources = await getDocs(profilesCollection);

    return resources.docs.map((resource) => resource.data());
  };

  const fetchAll = async (): Promise<Person[]> => {
    const snapshots = await getDocs(personsCollection);

    return snapshots.docs.map((personDoc) => personDoc.data());
  };

  const create = async ({ password, ...input }: NewPerson): Promise<Person> => {
    const resource = new Person(input);

    const insertUser = httpsCallable<Record<string, unknown>, Record<string, unknown>>(
      functions,
      'insertUserAsManager'
    );
    const result = await insertUser(instanceToPlain({ ...resource, password }, { exposeUnsetFields: false }));

    return plainToInstance(Person, result.data);
  };

  const update = async (input: UpdatePerson): Promise<void> => {
    const resource = new Person(input);

    const updateUser = httpsCallable<Record<string, unknown>, Record<string, unknown>>(functions, 'updateUserAsManager');
    await updateUser(instanceToPlain(resource, { exposeUnsetFields: false }));
  };

  const checkEmailValidation = async (email: string): Promise<EmailValidate> => {
    const emailValidation = httpsCallable<Record<string, unknown>, Record<string, unknown>>(
      functions,
      'checkEmailValidation'
    );

    const result = await emailValidation({ email });
    return plainToInstance(EmailValidate, result.data);
  };

  return { fetchById, fetchAll, fetchProfilesOf, create, update, checkEmailValidation };
};

export default usePersons;
